import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";
import { OrderPaymentMethod } from "./orderService";

const api = new APIClient();

const RESOURCE = "/settings/payment-methods";

export type PaymentMethodFlags = Record<string, boolean>;

export type DepositCollectionMethod =
  | "gateway_pix"
  | "external_pix"
  | "cash"
  | "card_terminal"
  | "other";

export const CHECKOUT_PAYMENT_METHOD_KEYS: OrderPaymentMethod[] = [
  "cash",
  "pix_external",
  "pix_gateway",
  "debit_card_terminal",
  "credit_card_terminal",
  "card_terminal_gateway",
];

export const DEPOSIT_COLLECTION_METHOD_KEYS: DepositCollectionMethod[] = [
  "external_pix",
  "gateway_pix",
  "cash",
  "card_terminal",
  "other",
];

export const CHECKOUT_GATEWAY_METHOD: OrderPaymentMethod = "pix_gateway";
export const CHECKOUT_TERMINAL_GATEWAY_METHOD: OrderPaymentMethod =
  "card_terminal_gateway";
export const DEPOSIT_GATEWAY_METHOD: DepositCollectionMethod = "gateway_pix";

export interface PaymentMethodsSettingsData {
  raw: {
    checkout: PaymentMethodFlags | null;
    deposit: PaymentMethodFlags | null;
  };
  effective: {
    checkout: string[];
    deposit: string[];
  };
}

export interface UpdatePaymentMethodSettingsPayload {
  enabled_checkout_payment_methods: PaymentMethodFlags;
  enabled_deposit_collection_methods: PaymentMethodFlags;
}

/** Fallback when API predates GAP-F1-220 (route missing on older backends). */
export const LEGACY_PAYMENT_METHODS_SETTINGS: PaymentMethodsSettingsData = {
  raw: {
    checkout: null,
    deposit: null,
  },
  effective: {
    checkout: [...CHECKOUT_PAYMENT_METHOD_KEYS],
    deposit: [...DEPOSIT_COLLECTION_METHOD_KEYS],
  },
};

const isPaymentMethodsRouteMissing = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const apiError = error as { code?: string };

  return apiError.code === "NOT_FOUND";
};

export const getPaymentMethodSettings = (): Promise<
  ApiResponse<PaymentMethodsSettingsData>
> => api.get<ApiResponse<PaymentMethodsSettingsData>>(RESOURCE);

/**
 * Tolerates homolog/prod API without `GET /settings/payment-methods` (pre-GAP-F1-220).
 * Settings page must still call {@link getPaymentMethodSettings} to surface 404.
 */
export const getMethodsWithLegacyFallback = async (): Promise<
  ApiResponse<PaymentMethodsSettingsData>
> => {
  try {
    return await getPaymentMethodSettings();
  } catch (error: unknown) {
    if (isPaymentMethodsRouteMissing(error)) {
      return {
        success: true,
        message: "",
        data: LEGACY_PAYMENT_METHODS_SETTINGS,
      };
    }

    throw error;
  }
};

export const updatePaymentMethodSettings = (
  payload: UpdatePaymentMethodSettingsPayload
): Promise<ApiResponse<PaymentMethodsSettingsData>> =>
  api.update<ApiResponse<PaymentMethodsSettingsData>>(RESOURCE, payload);

export const getMethods = getPaymentMethodSettings;

export const filterCheckoutMethodsByEffective = (
  effectiveCheckout: string[]
): OrderPaymentMethod[] =>
  CHECKOUT_PAYMENT_METHOD_KEYS.filter((method) => effectiveCheckout.includes(method));

export const filterDepositMethodsByEffective = (
  effectiveDeposit: string[]
): DepositCollectionMethod[] =>
  DEPOSIT_COLLECTION_METHOD_KEYS.filter((method) => effectiveDeposit.includes(method));

export const resolveDefaultCheckoutMethod = (
  stored: OrderPaymentMethod | null,
  available: OrderPaymentMethod[]
): OrderPaymentMethod | "" => {
  if (available.length === 0) {
    return "";
  }

  if (stored && available.includes(stored)) {
    return stored;
  }

  return available[0];
};

export const resolveDefaultDepositMethod = (
  current: DepositCollectionMethod | "" | null,
  available: DepositCollectionMethod[]
): DepositCollectionMethod | "" => {
  if (available.length === 0) {
    return "";
  }

  if (current && available.includes(current)) {
    return current;
  }

  return available[0];
};

export const buildCheckoutFlagsFromResponse = (
  raw: PaymentMethodFlags | null,
  effective: string[]
): PaymentMethodFlags => {
  if (raw) {
    return CHECKOUT_PAYMENT_METHOD_KEYS.reduce<PaymentMethodFlags>((flags, key) => {
      flags[key] = raw[key] ?? false;
      return flags;
    }, {});
  }

  return CHECKOUT_PAYMENT_METHOD_KEYS.reduce<PaymentMethodFlags>((flags, key) => {
    if (
      key === CHECKOUT_GATEWAY_METHOD ||
      key === CHECKOUT_TERMINAL_GATEWAY_METHOD
    ) {
      flags[key] = effective.includes(key);
      return flags;
    }

    flags[key] = true;
    return flags;
  }, {});
};

export const buildDepositFlagsFromResponse = (
  raw: PaymentMethodFlags | null,
  effective: string[]
): PaymentMethodFlags => {
  if (raw) {
    return DEPOSIT_COLLECTION_METHOD_KEYS.reduce<PaymentMethodFlags>((flags, key) => {
      flags[key] = raw[key] ?? false;
      return flags;
    }, {});
  }

  return DEPOSIT_COLLECTION_METHOD_KEYS.reduce<PaymentMethodFlags>((flags, key) => {
    flags[key] =
      key === DEPOSIT_GATEWAY_METHOD
        ? effective.includes(DEPOSIT_GATEWAY_METHOD)
        : true;
    return flags;
  }, {});
};
